/**
 * text-analysis.js — разбор текста: сегментация, слоги, читабельность.
 *
 * Зачем отдельный модуль. До 2026-09-17 всё это жило внутри блейда
 * /word-counter, и проверить его было нечем — а проверять было что: формулы
 * читабельности там стояли АНГЛИЙСКИЕ, а слоги считались регуляркой
 * `[^a-z]`, то есть у кириллицы их было НОЛЬ. Замер на живых текстах:
 *
 *   детская фраза «Кот сидит на окне…»            → Flesch 100 «очень легко»
 *   канцелярит «Функционирование институциональных
 *   механизмов государственного регулирования…»   → Flesch 100 «очень легко»
 *
 * То есть панель «Читабельность» была КОНСТАНТОЙ для всей русской аудитории
 * (а это больший шард: 5531 пользователь против 1540 на intl). Она не
 * ошибалась в деталях — она не измеряла ничего. Обращение #1693 (Динара,
 * 16.09.2026): «А что это за проверка сложности текста? Проверьте на ошибки,
 * если их найдёте, то работайте» — человек посмотрел на шкалы и не смог
 * понять, что они значат, потому что значить им было нечего.
 *
 * ⚠️⚠️ Урок шире самого дефекта: прибор, который ВСЕГДА отвечает одно и то
 * же, выглядит работающим. Ни одна проверка «страница отдала 200», ни один
 * скриншот, ни один глаз этого не видят — шкала нарисована, число стоит,
 * цвет зелёный. Уличает только ДВА входа с заведомо разным ответом, поэтому
 * сторож (tests/js/text-analysis.test.mjs) построен именно так: простой и
 * сложный текст на одном языке ОБЯЗАНЫ разойтись.
 *
 * Три вещи, которые модуль делает иначе, чем прежний код:
 *
 * 1. СЕГМЕНТАЦИЯ — `Intl.Segmenter` (Baseline с апреля 2024), а не
 *    `split(/[.!?]/)`. Замер на тех же строках: «Цена выросла на 3.5
 *    процента за год.» прежний код считал за 2 предложения, «Сумма 1,250.75
 *    руб. переведена.» — за 3, «Dr. Smith went to the U.S.A. yesterday.» — за
 *    5. А слов на предложение — вход КАЖДОЙ формулы читабельности, то есть
 *    один этот дефект портил и видимую плитку «Предложения», и все шесть
 *    индексов сразу.
 *    ⚠️ Segmenter НЕ панацея, и это проверено, а не предположено: он тоже
 *    режет после «Ю. А.» (3 вместо 1) и после «Dr.» (2 вместо 1) — в его
 *    правилах нет словаря сокращений. Поэтому поверх стоит СВОЙ заслон
 *    (ABBR + инициалы), а Segmenter отвечает за то, чего заслон не знает:
 *    десятичные дроби, многоточие, CJK-точку 。
 *    ⚠️ Цена замерена (node 24, 1.5 млн символов): sentence 7 мс, word
 *    40 мс, grapheme 87 мс. То есть сегментация НЕ была источником лага в
 *    4.5 с — им были облако слов и n-граммы, считавшиеся синхронно.
 *
 * 2. СЛОГИ ПО ПИСЬМЕННОСТИ. У кириллицы слог = гласная, и это правило, а не
 *    эвристика. У хангыля слог = слог-блок, тоже точно. У латиницы остаётся
 *    эвристика групп гласных. У арабицы огласовки на письме отсутствуют —
 *    слоги не считаются ВООБЩЕ, и слоговые формулы для неё не печатаются
 *    (вместо них LIX и ARI, которым слоги не нужны).
 *
 * 3. ФОРМУЛА ПОД ЯЗЫК, И ОНА НАЗВАНА. Flesch с английскими коэффициентами
 *    на русском тексте даёт бессмыслицу даже при верных слогах: русское
 *    слово длиннее (3.29 слога против 2.97), и 84.6 × ASW уводит результат
 *    в минус. Берём адаптации, у каждой есть автор и ссылка (см. MODELS), и
 *    ИМЯ модели уходит на экран: «что это за проверка» должно иметь ответ
 *    прямо в интерфейсе.
 *
 * Модуль чистый: ни DOM, ни сети, ни локали Laravel. Всё, что зависит от
 * языка интерфейса, вызывающий подставляет сам.
 */
(function (global) {
    'use strict';

    var HAS_SEG = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function';

    /* ═══════════════════════════════════════════════════════════════════
       ПИСЬМЕННОСТЬ
       Решает, какой слоговой счётчик и какая формула применимы. Смотрим на
       БУКВЫ, а не на всю строку: цифры и пунктуация одинаковы везде и
       только разбавляют голоса.
       ═══════════════════════════════════════════════════════════════════ */

    var RE = {
        cyr:   /[\u0400-\u04FF\u0500-\u052F]/g,
        latin: /[A-Za-z\u00C0-\u024F\u1E00-\u1EFF]/g,
        cjk:   /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF\u3040-\u309F\u30A0-\u30FF]/g,
        han:   /[\uAC00-\uD7AF\u1100-\u11FF]/g,
        arab:  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g,
        greek: /[\u0370-\u03FF\u1F00-\u1FFF]/g,
        hebr:  /[\u0590-\u05FF]/g,
        thai:  /[\u0E00-\u0E7F]/g,
        deva:  /[\u0900-\u097F]/g
    };

    function count(text, re) {
        re.lastIndex = 0;
        var m = text.match(re);
        return m ? m.length : 0;
    }

    /**
     * Доминирующая письменность текста.
     * Возвращает {script, share, counts} — share нужен, чтобы отличить
     * «текст на русском» от «английский текст с одним русским словом».
     */
    function detectScript(text) {
        if (!text) return { script: 'unknown', share: 0, counts: {} };
        var c = {
            cyrillic: count(text, RE.cyr),
            latin:    count(text, RE.latin),
            cjk:      count(text, RE.cjk),
            hangul:   count(text, RE.han),
            arabic:   count(text, RE.arab),
            greek:    count(text, RE.greek),
            hebrew:   count(text, RE.hebr),
            thai:     count(text, RE.thai),
            devanagari: count(text, RE.deva)
        };
        var total = 0, best = 'unknown', bestN = 0, k;
        for (k in c) if (c.hasOwnProperty(k)) {
            total += c[k];
            if (c[k] > bestN) { bestN = c[k]; best = k; }
        }
        if (!total) return { script: 'unknown', share: 0, counts: c };
        return { script: best, share: bestN / total, counts: c };
    }

    /* ═══════════════════════════════════════════════════════════════════
       СЕГМЕНТАЦИЯ
       ═══════════════════════════════════════════════════════════════════ */

    /**
     * Сокращения, после которых точка НЕ заканчивает предложение.
     * Список короткий и намеренно консервативный: лишнее склеивание двух
     * предложений искажает статистику так же, как лишний разрез, поэтому
     * сюда попадает только то, что в реальных текстах стоит перед строчной
     * буквой или числом. ⚠️ Без точки на конце — точка подставляется
     * проверкой.
     */
    var ABBR = {
        ru: ['т.е', 'т.к', 'т.д', 'т.п', 'т.н', 'и.о', 'др', 'пр', 'см', 'ср', 'стр', 'рис', 'табл',
             'гл', 'п', 'пп', 'ст', 'г', 'гг', 'в', 'вв', 'н.э', 'руб', 'коп', 'тыс', 'млн', 'млрд',
             'кг', 'км', 'см', 'мм', 'кв', 'куб', 'обл', 'респ', 'ул', 'просп', 'пер', 'д', 'корп',
             'кв', 'им', 'акад', 'проф', 'доц', 'канд', 'дис', 'изд', 'сост', 'ред', 'пример'],
        uk: ['т.д', 'т.п', 'т.зв', 'див', 'пор', 'стор', 'рис', 'табл', 'гл', 'ст', 'р', 'рр',
             'грн', 'коп', 'тис', 'млн', 'млрд', 'обл', 'вул', 'просп', 'пров', 'буд', 'кв', 'ім'],
        en: ['mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'vs', 'etc', 'e.g', 'i.e', 'al',
             'fig', 'no', 'vol', 'pp', 'ed', 'inc', 'ltd', 'co', 'corp', 'dept', 'est', 'approx',
             'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec'],
        de: ['dr', 'prof', 'hr', 'fr', 'nr', 'bzw', 'ca', 'evtl', 'ggf', 'inkl', 'usw', 'u.a',
             'z.b', 'd.h', 'vgl', 'abb', 'tab', 'str', 'bd', 'jh', 'mio', 'mrd'],
        es: ['sr', 'sra', 'srta', 'dr', 'dra', 'prof', 'núm', 'núms', 'pág', 'págs', 'etc', 'p.ej',
             'ee.uu', 'vol', 'cap', 'fig', 'ss', 'admón'],
        fr: ['m', 'mme', 'mlle', 'dr', 'pr', 'av', 'bd', 'ch', 'cf', 'etc', 'ex', 'fig', 'n°',
             'p', 'pp', 'vol', 'réf', 'env', 'st', 'ste'],
        pt: ['sr', 'sra', 'srta', 'dr', 'dra', 'prof', 'pág', 'págs', 'etc', 'p.ex', 'vol', 'cap',
             'fig', 'núm', 'av', 'ltda'],
        id: ['bpk', 'ibu', 'sdr', 'dr', 'prof', 'hal', 'no', 'dll', 'dsb', 'yth', 'tsb', 'jl'],
        ar: [],
        ko: []
    };

    /** Кэш собранных Set'ов — список один, а звать сегментацию будут часто. */
    var abbrCache = {};
    function abbrSet(lang) {
        if (abbrCache[lang]) return abbrCache[lang];
        var list = (ABBR[lang] || []).concat(ABBR.en);
        var s = Object.create(null);
        for (var i = 0; i < list.length; i++) s[list[i]] = true;
        abbrCache[lang] = s;
        return s;
    }

    /**
     * Заканчивается ли кусок текста сокращением или инициалом?
     * Инициал — одиночная ЗАГЛАВНАЯ буква с точкой («Ю.», «А.», «J.»):
     * именно на них Segmenter режет «Ю. А. Гагарин» на три предложения.
     */
    function endsWithAbbr(chunk, lang) {
        var t = chunk.replace(/\s+$/, '');
        if (t.charAt(t.length - 1) !== '.') return false;
        var body = t.slice(0, -1);
        /* инициал: последний токен — одна заглавная буква */
        var mInit = body.match(/(^|[\s(«"'])(\p{Lu})$/u);
        if (mInit) return true;
        /* сокращение: последнее «слово» (возможно с внутренними точками) */
        var mWord = body.match(/([\p{L}]+(?:\.[\p{L}]+)*)$/u);
        if (!mWord) return false;
        var w = mWord[1].toLowerCase();
        var set = abbrSet(lang);
        if (set[w]) return true;
        /* «U.S.A» / «т.е» — цепочка однобуквенных кусков это тоже сокращение */
        if (/^(?:\p{L}\.)+\p{L}$/u.test(mWord[1])) return true;
        return false;
    }

    /* Экземпляры Intl.Segmenter — создаются заново дорого, а зовём мы их
       по одному разу НА ПРЕДЛОЖЕНИЕ (у длинного текста это тысячи вызовов).
       Ключ — локаль плюс гранулярность; объектов получается единицы. */
    var segCache = Object.create(null);
    function seg(locale, granularity) {
        var key = (locale || '-') + '|' + granularity;
        var s = segCache[key];
        if (s === undefined) {
            try { s = new Intl.Segmenter(locale || undefined, { granularity: granularity }); }
            catch (e) { s = null; }
            segCache[key] = s;
        }
        return s;
    }

    /**
     * Предложения. Возвращает массив строк (без пустых).
     *
     * Лестница: Intl.Segmenter → регулярка. Разница между ними видна только
     * на десятичных дробях и многоточии, но ровно они и встречаются в живых
     * текстах чаще всего.
     */
    function sentences(text, lang) {
        if (!text || !text.trim()) return [];
        var raw = [];
        if (HAS_SEG) {
            try {
                var sg = seg(lang, 'sentence');
                if (!sg) throw 0;
                var it = sg.segment(text);
                for (var s of it) raw.push(s.segment);
            } catch (e) { raw = []; }
        }
        if (!raw.length) {
            /* Запасной путь: режем по терминатору + пробел + начало реплики.
               Требование «дальше идёт пробел» само по себе снимает десятичные
               дроби («3.5»), ради которых прежний код и ломался.
               Без lookbehind: старый WebKit (iOS < 16.4) роняет ВЕСЬ файл на
               разборе такой регулярки (NoRegexLookbehindTest) — поэтому
               сентинел ставится ПОСЛЕ терминатора, а режем по нему. Тот же
               результат, что у прежнего split с ретро-просмотром терминатора, —
               проверено на 21 ручном случае и 20 000 случайных строк (17.09.2026). */
            var SEP = String.fromCharCode(0);   // a byte no text carries; spelled as a call so the source file stays clean
            raw = text.replace(/([.!?…。！？])\s+/g, '$1' + SEP).split(SEP);
        }

        /* Заслон сокращений: склеиваем кусок со следующим, если он кончается
           сокращением или инициалом. */
        var out = [], buf = '';
        for (var i = 0; i < raw.length; i++) {
            buf += raw[i];
            var last = i === raw.length - 1;
            if (!last && endsWithAbbr(buf, lang)) continue;
            if (buf.trim()) out.push(buf);
            buf = '';
        }
        if (buf.trim()) out.push(buf);
        return out;
    }

    /**
     * Слова.
     *
     * `Intl.Segmenter` с granularity:'word' — единственный способ
     * посчитать тайский, лаосский и кхмерский, где пробелов между словами
     * нет вовсе: прежний `split(/\s+/)` давал там 1 слово на весь абзац.
     * Замер: «สวัสดีครับผมชอบกิน» → 5 слов против 1.
     *
     * ⚠️ CJK остаётся по СИМВОЛАМ, и это не упущение, а издательская норма:
     * в китайском и японском объём считают в 文字数 — знаках. Segmenter дал бы
     * «これはペンです» = 4, что лингвистически верно и никем не используется
     * как мера объёма. Поэтому CJK-символы считаем поштучно, а панель об
     * этом честно пишет.
     */
    function words(text, lang) {
        if (!text || !text.trim()) return [];
        if (HAS_SEG) {
            try {
                var sg = seg(lang, 'word');
                if (!sg) throw 0;
                var out = [];
                for (var s of sg.segment(text)) {
                    if (!s.isWordLike) continue;
                    var w = s.segment;
                    RE.cjk.lastIndex = 0;
                    if (RE.cjk.test(w) && w.length > 1) {
                        /* CJK-кусок разбираем на знаки — см. комментарий выше */
                        for (var ch of w) out.push(ch);
                    } else {
                        out.push(w);
                    }
                }
                return out;
            } catch (e) { /* падаем в регулярку */ }
        }
        var t = text.replace(RE.cjk, ' $& ');
        return t.split(/\s+/).filter(function (w) { return w.length > 0; });
    }

    /** Графемы — «символы» так, как их видит человек (эмодзи = 1). */
    function graphemes(text) {
        if (!text) return 0;
        if (HAS_SEG) {
            try {
                var sg = seg(null, 'grapheme');
                if (!sg) throw 0;
                var n = 0;
                for (var _ of sg.segment(text)) n++;
                return n;
            } catch (e) { /* ниже */ }
        }
        return Array.from(text).length;
    }

    /* ═══════════════════════════════════════════════════════════════════
       СЛОГИ
       ═══════════════════════════════════════════════════════════════════ */

    var VOWELS = {
        cyrillic: /[\u0430\u0435\u0451\u0438\u043E\u0443\u044B\u044D\u044E\u044F\u0454\u0456\u0457]/gi,
        greek: /[\u03B1\u03B5\u03B7\u03B9\u03BF\u03C5\u03C9\u03AC\u03AD\u03AE\u03AF\u03CC\u03CD\u03CE]/gi,
        latin:    null /* эвристика ниже */
    };

    /** Латиница: группы гласных минус немая «e», минимум 1. */
    function syllablesLatin(word) {
        var w = word.toLowerCase().replace(/[^a-zà-öø-ÿ]/g, '');
        if (!w) return 0;
        if (w.length <= 3) return 1;
        w = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
        w = w.replace(/^y/, '');
        var m = w.match(/[aeiouyà-öø-ÿ]{1,2}/g);
        return m ? Math.max(m.length, 1) : 1;
    }

    /**
     * Слоги одного слова по письменности.
     * `null` = «для этой письменности слоги не считаются» (арабица: коротких
     * гласных на письме нет, любая цифра была бы выдумкой).
     */
    function syllablesOf(word, script) {
        if (!word) return 0;
        switch (script) {
            case 'cyrillic': {
                VOWELS.cyrillic.lastIndex = 0;
                var m = word.match(VOWELS.cyrillic);
                return m ? m.length : 0;
            }
            case 'greek': {
                VOWELS.greek.lastIndex = 0;
                var g = word.match(VOWELS.greek);
                return g ? g.length : 0;
            }
            case 'hangul': {
                /* Слог-блок хангыля и есть слог — считаем блоки. */
                var n = 0;
                for (var ch of word) {
                    var c = ch.codePointAt(0);
                    if (c >= 0xAC00 && c <= 0xD7A3) n++;
                }
                return n;
            }
            case 'cjk':
                /* Каждый иероглиф / кана — примерно один слог (мора). */
                return Array.from(word).length;
            case 'latin':
                return syllablesLatin(word);
            case 'arabic':
            case 'hebrew':
            case 'thai':
            case 'devanagari':
                return null;
            default:
                return syllablesLatin(word);
        }
    }

    /** Сумма слогов; вернёт null, если письменность их не даёт. */
    function totalSyllables(wordList, script) {
        if (syllablesOf('x', script) === null) return null;
        var sum = 0;
        for (var i = 0; i < wordList.length; i++) {
            var s = syllablesOf(wordList[i], script);
            if (s !== null) sum += s;
        }
        return sum;
    }

    /* ═══════════════════════════════════════════════════════════════════
       ФОРМУЛЫ ЧИТАБЕЛЬНОСТИ
       Каждая запись называет автора адаптации — имя уходит на экран, чтобы
       у вопроса «что это за проверка» был ответ в самом интерфейсе.
       ═══════════════════════════════════════════════════════════════════ */

    var MODELS = {
        /* ASL = слов на предложение, ASW = слогов на слово */
        en: {
            name: 'Flesch – Kincaid',
            ease: function (ASL, ASW) { return 206.835 - 1.015 * ASL - 84.6 * ASW; },
            grade: function (ASL, ASW) { return 0.39 * ASL + 11.8 * ASW - 15.59; }
        },
        ru: {
            /* И. В. Оборнева, 2006 — пересчёт коэффициентов Флеша на корпусе
               из 100 русских текстов. Русское слово длиннее английского
               (3.29 слога против 2.97), поэтому английские 84.6 уводят
               результат в минус на любом нормальном тексте. */
            name: 'Флеш – Оборнева',
            ease: function (ASL, ASW) { return 206.835 - 1.3 * ASL - 60.1 * ASW; },
            grade: function (ASL, ASW) { return 0.5 * ASL + 8.4 * ASW - 15.59; }
        },
        de: {
            /* Amstad, 1978 */
            name: 'Flesch – Amstad',
            ease: function (ASL, ASW) { return 180 - ASL - 58.5 * ASW; },
            grade: function (ASL, ASW) { return 0.39 * ASL + 11.8 * ASW - 15.59; }
        },
        es: {
            /* Fernández Huerta, 1959 */
            name: 'Fernández Huerta',
            ease: function (ASL, ASW) { return 206.84 - 1.02 * ASL - 60 * ASW; },
            grade: function (ASL, ASW) { return 0.39 * ASL + 11.8 * ASW - 15.59; }
        },
        fr: {
            /* Kandel & Moles, 1958 */
            name: 'Kandel – Moles',
            ease: function (ASL, ASW) { return 207 - 1.015 * ASL - 73.6 * ASW; },
            grade: function (ASL, ASW) { return 0.39 * ASL + 11.8 * ASW - 15.59; }
        },
        pt: {
            /* Martins и др., адаптация Флеша на португальский */
            name: 'Flesch (PT)',
            ease: function (ASL, ASW) { return 248.835 - 1.015 * ASL - 84.6 * ASW; },
            grade: function (ASL, ASW) { return 0.39 * ASL + 11.8 * ASW - 15.59; }
        }
    };
    MODELS.uk = { name: 'Флеш – Оборнева', ease: MODELS.ru.ease, grade: MODELS.ru.grade };

    /** Какая модель применима к этому тексту. */
    function pickModel(script, lang) {
        if (script === 'cyrillic') return { key: lang === 'uk' ? 'uk' : 'ru', model: MODELS.ru, exact: true };
        if (script === 'latin') {
            if (MODELS[lang] && lang !== 'ru' && lang !== 'uk') return { key: lang, model: MODELS[lang], exact: true };
            return { key: 'en', model: MODELS.en, exact: lang === 'en' };
        }
        return { key: null, model: null, exact: false };
    }

    /* Буквы и цифры одним совпадением на слово — см. комментарий в
       readability(): посимвольная проверка стоила секунд на длинном тексте. */
    var LETTERS_RE = /[\p{L}\p{N}]/gu;

    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
    function r1(v) { return Math.round(v * 10) / 10; }

    /**
     * Полный разбор читабельности.
     *
     * Отдаёт не только числа, но и ЧЕМ они получены: `model` (имя формулы),
     * `syllabic` (были ли слоги вообще доступны) и `applicable` — можно ли
     * доверять слоговым индексам для этой письменности. Вызывающий обязан
     * показать это человеку: безымянная шкала и была дефектом.
     *
     * @param {object} p {words:[], sentences:[], text, script, lang}
     */
    function readability(p) {
        var W = p.words.length, S = p.sentences.length;
        var script = p.script || 'latin';
        var out = {
            model: null, modelKey: null, exact: false, syllabic: false,
            words: W, sentences: S, syllables: null,
            ASL: 0, ASW: 0,
            ease: null, grade: null, fog: null, smog: null, coleman: null, ari: null, lix: null,
            level: null, complexWords: 0, longWords: 0
        };
        if (!W || !S) return out;

        var ASL = W / S;
        out.ASL = r1(ASL);

        /* Длинные слова (7+ букв) — вход LIX, слогов не требует, поэтому
           считается для ЛЮБОЙ письменности. */
        var longW = 0, i, w;
        for (i = 0; i < W; i++) {
            if (Array.from(p.words[i]).length >= 7) longW++;
        }
        out.longWords = longW;

        /* LIX (Björnsson) — тем и ценен, что не зависит от слогов, а значит
           применим там, где Flesch неприменим принципиально (арабица, иврит,
           тайский). Для них он остаётся ЕДИНСТВЕННЫМ честным индексом. */
        out.lix = r1(ASL + 100 * (longW / W));

        /* ARI и Coleman-Liau считаются по БУКВАМ, а не по слогам. Прежний код
           брал только `[a-zA-ZÀ-ɏ]`, то есть у кириллицы букв было
           ноль и Coleman-Liau всегда выходил 0. */
        var letters = 0;
        for (i = 0; i < W; i++) {
            w = p.words[i];
            /* ⚠️ Здесь стоял `for (var ch of w) if (/\p{L}|\p{N}/u.test(ch))` —
               НОВАЯ регулярка и отдельная проверка на КАЖДЫЙ символ текста.
               На 282 000 знаков это давало задачу главного потока в секунды.
               Одно совпадение на слово делает ту же работу. */
            LETTERS_RE.lastIndex = 0;
            var lm = w.match(LETTERS_RE);
            if (lm) letters += lm.length;
        }
        var L = (letters / W) * 100, Sp = (S / W) * 100;
        out.coleman = r1(Math.max(0, 0.0588 * L - 0.296 * Sp - 15.8));
        out.ari = r1(Math.max(0, 4.71 * (letters / W) + 0.5 * ASL - 21.43));

        var syl = totalSyllables(p.words, script);
        if (syl === null) {
            /* Письменность не даёт слогов — слоговые индексы НЕ выдумываем. */
            out.level = null;
            return out;
        }
        out.syllabic = true;
        out.syllables = syl;
        var ASW = syl / W;
        out.ASW = Math.round(ASW * 100) / 100;

        var picked = pickModel(script, p.lang);
        if (!picked.model) {
            /* Слоги есть (CJK, хангыль), а калиброванной формулы нет.
               Печатать под чужими коэффициентами не будем. */
            return out;
        }
        out.model = picked.model.name;
        out.modelKey = picked.key;
        out.exact = picked.exact;
        out.ease = r1(clamp(picked.model.ease(ASL, ASW), 0, 100));
        out.grade = r1(Math.max(0, picked.model.grade(ASL, ASW)));

        /* Сложные слова = 3+ слога. Вход Gunning Fog и SMOG. */
        var complex = 0;
        for (i = 0; i < W; i++) {
            var sv = syllablesOf(p.words[i], script);
            if (sv !== null && sv >= 3) complex++;
        }
        out.complexWords = complex;
        out.fog = r1(Math.max(0, 0.4 * (ASL + 100 * (complex / W))));
        out.smog = S >= 3 ? r1(3.1291 + 1.043 * Math.sqrt(complex * (30 / S))) : null;
        out.level = easeLevel(out.ease);
        return out;
    }

    /** 0…100 → ступень 0..4 (очень легко … очень сложно). Тексты не красим. */
    function easeLevel(ease) {
        if (ease === null) return null;
        if (ease >= 80) return 0;
        if (ease >= 60) return 1;
        if (ease >= 40) return 2;
        if (ease >= 20) return 3;
        return 4;
    }

    global.TextAnalysis = {
        HAS_SEGMENTER: HAS_SEG,
        detectScript: detectScript,
        sentences: sentences,
        words: words,
        graphemes: graphemes,
        syllablesOf: syllablesOf,
        totalSyllables: totalSyllables,
        readability: readability,
        easeLevel: easeLevel,
        endsWithAbbr: endsWithAbbr,
        MODELS: MODELS
    };
})(typeof window !== 'undefined' ? window : globalThis);
